import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Badge, Button, Card, DisabledText, Divider, Inline, Input, Stack, SubHeadline } from "@KristianIV/common-ui";
import { ApplicationState } from "../../reducers";
import {
  resetProperty,
  setDetectorForScout,
  setGainForScout,
  setModelForScout,
  setPredictionThresholdForScout,
  setTimeServiceForScout,
  setTimingThresholdForScout,
  stopUploadAudioEvents,
  uploadAudioEvents,
} from "../../services/organisationService";
import { Scout } from "../model/scout";
import styles from "../OrganisationPage.module.css";

type Value = string | number | undefined;

interface ConfigField {
  /** Property name the backend uses for reset. */
  property: string;
  label: string;
  override: (scout: Scout) => Value;
  /** What the device last reported; absent for settings the status message does not carry. */
  reported?: (scout: Scout) => Value;
  save: (scout: Scout, value: string, jwtToken: string) => void;
}

// The same settings, names and endpoints as Commander's admin overlay.
const FIELDS: ConfigField[] = [
  {
    property: "predictionThreshold",
    label: "Gunshot threshold",
    override: (s) => s.predictionThreshold,
    reported: (s) => s.status.predictionThreshold,
    save: (s, v, jwt) => setPredictionThresholdForScout(s.deviceId, v, jwt),
  },
  {
    property: "timingThreshold",
    label: "Timing threshold",
    override: (s) => s.timingThreshold,
    save: (s, v, jwt) => setTimingThresholdForScout(s.deviceId, v, jwt),
  },
  {
    property: "model",
    label: "Krazy8 model",
    override: (s) => s.model,
    reported: (s) => s.status.model,
    save: (s, v, jwt) => setModelForScout(s.deviceId, v, jwt),
  },
  {
    property: "detector",
    label: "Gale model",
    override: (s) => s.detectorId,
    reported: (s) => s.status.detectorId,
    save: (s, v, jwt) => setDetectorForScout(s.deviceId, v, jwt),
  },
  {
    property: "gain",
    label: "Gain",
    override: (s) => s.gain,
    reported: (s) => s.status.gain,
    save: (s, v, jwt) => setGainForScout(s.deviceId, v, jwt),
  },
  {
    property: "timeService",
    label: "Time service",
    override: (s) => s.timeServiceName,
    reported: (s) => s.status.timeServiceName,
    save: (s, v, jwt) => setTimeServiceForScout(s.deviceId, v, jwt),
  },
];

const isSet = (value: Value) => value !== undefined && value !== null;
const asText = (value: Value) => (isSet(value) ? String(value) : "");

const OverrideInput = ({
  value,
  disabled,
  onCommit,
}: {
  value: Value;
  disabled: boolean;
  onCommit: (value: string) => void;
}) => {
  const [draft, setDraft] = useState(asText(value));
  useEffect(() => {
    setDraft(asText(value));
  }, [value]);

  const commit = () => {
    const trimmed = draft.trim();
    if (trimmed !== "" && trimmed !== asText(value)) {
      onCommit(trimmed);
    }
  };

  return (
    <Input
      value={draft}
      placeholder="Not specified"
      disabled={disabled}
      onChange={(event) => setDraft(event.target.value)}
      onBlur={() => setDraft(asText(value))}
      onKeyDown={(event) => {
        if (event.key === "Enter") {
          commit();
          event.currentTarget.blur();
        }
      }}
    />
  );
};

/** Upload of the audio around detections, on or off; the status reports the current state. */
const AudioUpload = ({ scout, jwtToken }: { scout: Scout; jwtToken: string | undefined }) => {
  const uploading = scout.status.upload === true;
  const toggle = () => {
    if (!jwtToken) {
      return;
    }
    const call = uploading ? stopUploadAudioEvents : uploadAudioEvents;
    call(scout.deviceId, jwtToken).catch((err) => console.error("Failed to change audio upload", err));
  };
  return (
    <Inline justify="between" gap="lg">
      <Inline gap="md">
        <span className={styles.configLabel}>Audio upload</span>
        <Badge color={uploading ? "green" : undefined}>{uploading ? "On" : "Off"}</Badge>
      </Inline>
      <Button size="small" disabled={!jwtToken} onClick={toggle}>
        {uploading ? "Stop uploading" : "Start uploading"}
      </Button>
    </Inline>
  );
};

const DeviceConfiguration = ({ scout }: { scout: Scout }) => {
  const jwtToken = useSelector((state: ApplicationState) => state.organisation.userJwtToken);

  return (
    <Card interactive={false} padded>
      <Stack gap="lg">
        <Inline justify="between" gap="lg">
          <SubHeadline noMargin>Override configuration</SubHeadline>
          <DisabledText>
            {jwtToken
              ? "Press Enter to push an override to the device; it shows as Syncing until the device reports it back"
              : "Waiting for the organisation login before overrides can be changed"}
          </DisabledText>
        </Inline>
        <div className={styles.configGrid}>
          <div className={styles.configHead}>Setting</div>
          <div className={styles.configHead}>Reported by device</div>
          <div className={styles.configHead}>Override</div>
          <div className={styles.configHead} />
          <div className={styles.configHead} />

          {FIELDS.map((field) => {
            const override = field.override(scout);
            const reported = field.reported?.(scout);
            const overridden = isSet(override);
            // Settings the status does not carry cannot be confirmed, so they never show as syncing.
            const syncing = overridden && field.reported !== undefined && asText(override) !== asText(reported);
            return (
              <React.Fragment key={field.property}>
                <div className={styles.configCell}>
                  <span className={styles.configLabel}>{field.label}</span>
                </div>
                <div className={`${styles.configCell} ${styles.mono}`}>{isSet(reported) ? String(reported) : "—"}</div>
                <div className={styles.configCell}>
                  <OverrideInput
                    value={override}
                    disabled={!jwtToken}
                    onCommit={(value) => jwtToken && field.save(scout, value, jwtToken)}
                  />
                </div>
                <div className={styles.configCell}>
                  {syncing ? <Badge color="amber">Syncing…</Badge> : overridden ? <Badge color="blue">Overridden</Badge> : null}
                </div>
                <div className={`${styles.configCell} ${styles.alignEnd}`}>
                  <Button
                    size="small"
                    disabled={!overridden || !jwtToken}
                    onClick={() => jwtToken && resetProperty(scout.deviceId, field.property, jwtToken)}
                  >
                    Reset
                  </Button>
                </div>
              </React.Fragment>
            );
          })}
        </div>
        <Divider />
        <AudioUpload scout={scout} jwtToken={jwtToken} />
      </Stack>
    </Card>
  );
};

export default DeviceConfiguration;
